import { useNotification } from "@expert/common-ui";
import { useSalesNarrativeContent } from "@expert/contentful";
import { useDisclosure } from "@mantine/hooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { SendChannel } from "../SendLinkCard/SendLinkCard";

import { postDeclination } from "../../../api/declination";
import { isMaxNotificationAttemptsError } from "../../../api/sendNotification.errors";
import { useSalesContext } from "../../../sales-context/SalesContext";
import { sendSalesErrorMetric } from "../../../shared-utils";
import { useDetectedCuesStore, useProductOfferStore } from "../../../stores";
import { SaleProgress } from "../../../types";
import { GuidedBrowsingModal, useGuidedBrowsingStore } from "../../guided-browsing";
import { useLanguageStore } from "../../product-sale-flow/components/language-selector/useLanguageStore";
import { useHandleSendLink } from "../../product-sale-flow/components/SendLink/HandleSendLink";
import { VHDP_PRODUCT_SKU } from "../constants";
import { filterNarrativeForGuidedBrowsing } from "../fallbackNarrative";
import { NarrativeAIState } from "../NarrativeAIState/NarrativeAIState";
import { NarrativeContainer } from "../NarrativeContainer/NarrativeContainer";
import { NarrativeFooter } from "../NarrativeFooter/NarrativeFooter";
import { narrativeFromContent } from "../narrativeFromContent";
import { NarrativeHeader } from "../NarrativeHeader/NarrativeHeader";
import { NarrativePanel } from "../NarrativePanel/NarrativePanel";
import { NarrativeRebuttalCard } from "../NarrativeRebuttalCard/NarrativeRebuttalCard";
import { PlanDetailsModal } from "../PlanDetails/PlanDetailsModal";
import { vhdpPlanData } from "../PlanDetails/vhdpPlanData";
import { channelToCommType, channelToDeliveryMethod, toContactValue } from "../sendContact";
import { useLongFormAiStore } from "../store/useLongFormAiStore";
import { createLfaTelemetry, isBeatRequiredById } from "../telemetry";
import { verizonLogo } from "../verizonLogo.demo";

/**
 * The Long Form AI sale surface, bound to `useLongFormAiStore`. `ProductSaleFlow` renders this in
 * place of the legacy offer-checklist flow when the flag is on for the VHDP pilot (openspec
 * long-form-ai) — never a separate pane, and with no in-surface fallback to the checklist.
 *
 * Content is either dynamic (Data Science → WebSocket → `receiveNarrativeEvent`) or the static client
 * `fallbackNarrative`. The surface waits for the producer; the expert can reveal the fallback instead.
 * Revealing the fallback opts the session out of producer narratives (`receiveNarrativeEvent` drops
 * them); a producer narrative that arrived first is replaced only by a later one, staged behind a
 * banner. The source is recorded in logs/analytics.
 *
 * The Guide + Enroll steps' send-link forms fire the REAL sales behavior via `useHandleSendLink`
 * (the same seam the legacy checklist uses): guided browsing opens the existing co-browse dialog,
 * enrollment sends the smart link. Completion (customer enrolls, either way) arrives asynchronously
 * as the usual `enrollment-event` → `saleProgress: Success`, at which point `ProductSaleFlow`'s VHDP
 * gate falls through to the legacy success screen. "View plan details" opens `PlanDetailsModal`.
 *
 * Parity guards inherited from the legacy flow: sends and decline are inert in an Expert University
 * training session (`isSimulatedVoiceTask`) so a simulated call can never contact a real customer,
 * guided browsing follows the flag + product config rather than being assumed on, and product content
 * is read in the session's selected language.
 */
export const LongFormAiPanel = () => {
    const { sessionId, employeeId, partner, logger, analytics, features, isSimulatedVoiceTask, apiClients } =
        useSalesContext();
    const [planOpen, { open: openPlan, close: closePlan }] = useDisclosure(false);
    // Quick rebuttal shows INLINE in this surface (not the Expert Assist tab) — see the footer wiring.
    const [rebuttalOpen, { open: openRebuttal, close: closeRebuttal }] = useDisclosure(false);
    const notification = useNotification();
    const language = useLanguageStore((s) => s.language);

    const session = useLongFormAiStore((s) => s.getSession(sessionId));
    // Keyed on the pilot SKU, not `session.sku`. Plan details are static client data seeded below,
    // whereas `session.sku` is only written by an inbound narrative event or `loadFallbackNarrative` —
    // neither of which runs on a content miss, so deriving from it hid "View plan details" in exactly
    // the dead-end state this panel now has to stay usable in. The panel only ever renders for VHDP.
    const planData = useLongFormAiStore((s) => s.getPlanDetails(VHDP_PRODUCT_SKU));
    const markRead = useLongFormAiStore((s) => s.markRead);
    const markSkipped = useLongFormAiStore((s) => s.markSkipped);
    const setAwaitingEnrollment = useLongFormAiStore((s) => s.setAwaitingEnrollment);
    const setAwaitingGuide = useLongFormAiStore((s) => s.setAwaitingGuide);
    const reopenBeat = useLongFormAiStore((s) => s.reopenBeat);
    const loadFallbackNarrative = useLongFormAiStore((s) => s.loadFallbackNarrative);
    const setPlanDetails = useLongFormAiStore((s) => s.setPlanDetails);
    const markNarrativeShown = useLongFormAiStore((s) => s.markNarrativeShown);
    const applyPendingNarrative = useLongFormAiStore((s) => s.applyPendingNarrative);

    const receiveGuidedBrowsing = useGuidedBrowsingStore((s) => s.receiveData);
    const closeGuidedBrowsing = useGuidedBrowsingStore((s) => s.closeModal);
    const updateSmartLinkMeta = useProductOfferStore((s) => s.updateSmartLinkMetaBySessionId);
    const updateProduct = useProductOfferStore((s) => s.updateProduct);

    // The selected product is the sale's subject — its name drives the header, its offer id keys the
    // smart-link limit, its productSku/id identify it to declination, and its Contentful
    // `guidedBrowsing` config (read in the session language) controls co-browse behavior.
    const selectedProduct = useProductOfferStore((s) => s.getSelectedProduct(sessionId));
    const selectedProductInfo = useProductOfferStore((s) =>
        selectedProduct ? s.getProductInfo(sessionId, selectedProduct.productSku, language) : undefined,
    );
    const selectedOfferId = selectedProduct?.id;
    const enrollResendDisabled = useProductOfferStore((s) =>
        selectedOfferId ? s.getSmartLinkMetaBySessionId(sessionId, selectedOfferId).maximumSmartLinksGenerated : false,
    );
    const productName = selectedProductInfo?.displayName ?? planData?.meta.productName ?? session.sku ?? "Offer";

    // Guided browsing follows the flag AND the product's Contentful config, exactly as the legacy flow
    // derives it (`IntegratedFlow.tsx`) — never assumed on, so a `guide` step can't send a co-browse
    // link the partner/product hasn't opted into.
    const isGuidedBrowsing = features.isGuidedBrowsingEnabled && !!selectedProductInfo?.guidedBrowsing;

    // Real send-link seams (same hook the legacy checklist uses).
    const sendGuidedBrowsingLink = useHandleSendLink({ isGuidedBrowsing });
    const sendEnrollmentLink = useHandleSendLink({ isGuidedBrowsing: false });

    // Guards a decline request in flight (mirrors the legacy SalesFooter's `declining`).
    const [declining, setDeclining] = useState(false);
    // Reset per-sale UI state only on a genuine NEW SESSION — NOT on a same-session remount. A
    // useEffect keyed on sessionId still fires on every mount, so without this ref guard a remount
    // within the same session (e.g. ProductSaleFlow toggling shouldShowLongFormAi) would clear the
    // waiting flags and re-arm a duplicate real co-browse/enroll link. The store deliberately
    // persists awaitingGuide/awaitingEnrollment across unmount/remount; this preserves that.
    const prevSessionIdRef = useRef(sessionId);
    useEffect(() => {
        if (prevSessionIdRef.current === sessionId) return;
        prevSessionIdRef.current = sessionId;
        setAwaitingGuide(sessionId, false);
        setAwaitingEnrollment(sessionId, false);
        closeRebuttal();
    }, [sessionId, setAwaitingGuide, setAwaitingEnrollment, closeRebuttal]);

    const cues = useDetectedCuesStore((s) => s.getDisplayCues(sessionId));
    const cueNames = useMemo(() => cues.filter((cue) => cue.detected).map((cue) => cue.name), [cues]);

    // Adoption/usage telemetry (openspec long-form-ai §7). Every LFA business event carries these dims
    // so the OpenSearch dashboard can slice by session/expert/product/partner. `isFallback` comes from
    // the store, so producer adoption and fallback rate stay distinguishable.
    const telemetry = useMemo(
        () =>
            createLfaTelemetry((name, data) => analytics.dispatcher.dispatchBusinessEvent(name, data), {
                sessionId,
                employeeId,
                productSku: session.sku ?? VHDP_PRODUCT_SKU,
                partner,
                isFallback: session.isFallback,
                pitchType: session.pitchType,
            }),
        [analytics.dispatcher, sessionId, employeeId, session.sku, partner, session.isFallback, session.pitchType],
    );

    // The fallback copy comes from Contentful, the same entries Data Science reads — one source, two
    // consumers. No cache and no hardcoded last resort: a Contentful outage already breaks the sale
    // flow (product/offer content, cue vocabulary, plan cards), so this inherits an existing single
    // point of failure rather than adding one.
    const {
        content,
        isPending: contentPending,
        isError: contentFailed,
    } = useSalesNarrativeContent(VHDP_PRODUCT_SKU, language);

    // Filtered on `isGuidedBrowsing` (the flag AND the product's Contentful config) — NOT the raw flag.
    // With the flag on but the product carrying no `guidedBrowsing` config, the raw flag would keep
    // Guide and drop Enroll, stranding the expert with no enrollment path.
    const filteredFallback = useMemo(
        () => (content ? filterNarrativeForGuidedBrowsing(narrativeFromContent(content), isGuidedBrowsing) : undefined),
        [content, isGuidedBrowsing],
    );

    // The surface opens WAITING for a producer narrative and never loads the fallback on its own. The
    // expert reveals it instead — deliberately expert-triggered rather than on a timer, so nobody is
    // switched off the personalized script by a clock while they are mid-sentence.
    //
    // Read the narrative back out of the store rather than trusting `session` from this render. The
    // button only exists while there is no narrative, but a producer event can land between paint and
    // click — and at that moment the closure's `session.narrative` is still the stale `null`, so
    // guarding on it would let the fallback overwrite a narrative that had already arrived. The store
    // also refuses the write; this half keeps the log and the FallbackShown event from firing for a
    // reveal that never happened.
    const showFallback = useCallback(() => {
        if (useLongFormAiStore.getState().getSession(sessionId).narrative || !filteredFallback) return;
        logger.warn({ sku: VHDP_PRODUCT_SKU }, "[LFA] expert revealed the fallback narrative");
        // `trigger` marks "the expert gave up waiting" — the signal that the producer is too slow or dark.
        void analytics.dispatcher.dispatchBusinessEvent("LongFormAiFallbackShown", { trigger: "expert" });
        loadFallbackNarrative(sessionId, filteredFallback, VHDP_PRODUCT_SKU, { expertChosen: true });
    }, [filteredFallback, sessionId, loadFallbackNarrative, logger, analytics]);

    // A replacement narrative is staged, not applied (store `pendingNarrative`) — the expert loads it
    // from the banner. Beat progress carries over by beat id, so loading mid-sale does not re-read.
    //
    // Swap FIRST, then report. The opposite order — which the funnel OPEN effect below uses on purpose —
    // is right when the risk is losing an event, but here it is the expert's CLICK at stake: a throwing
    // dispatch would leave the banner up and the script never swapped. Losing one analytics event beats
    // wedging the surface mid-call.
    const loadNewScript = useCallback(() => {
        applyPendingNarrative(sessionId);
        telemetry.newScriptLoaded(session.pendingPitchType);
    }, [telemetry, applyPendingNarrative, sessionId, session.pendingPitchType]);

    // The funnel's OPEN event, so it must fire for every narrative, whether a producer's or the
    // expert-revealed fallback.
    //
    // Fires once per sale, and the guard lives in the STORE rather than a ref because a ref is
    // recreated on remount and would double-count. `isFallback` therefore records whichever source
    // opened the sale; when a staged producer script replaces another, downstream events carry the new
    // script's `pitchType` while OPEN keeps the original. That asymmetry is intended — one sale is one open.
    useEffect(() => {
        if (!session.narrative || session.narrativeShown) return;
        // Dispatch BEFORE setting the guard. The other order means a throwing dispatch still marks the
        // event as sent, so the effect early-returns forever and the funnel loses its open entirely.
        telemetry.narrativeShown();
        markNarrativeShown(sessionId);
    }, [session.narrative, session.narrativeShown, sessionId, markNarrativeShown, telemetry]);

    // The "View plan details" reference content is static client data (Contentful feeds it
    // later). Seed it for the pilot SKU so the header's "View plan details" link + modal work.
    useEffect(() => {
        setPlanDetails(VHDP_PRODUCT_SKU, vhdpPlanData);
    }, [setPlanDetails]);

    // No narrative yet means we are still waiting on the PRODUCER — the normal state, not a failure — so
    // the panel shows the waiting card rather than an empty surface. The fallback is never substituted
    // here; it is reached by the expert's own click.
    const narrative = session.narrative;
    // The fallback COPY's own health is a separate axis from producer silence, and the expert needs to
    // tell "be patient" from "the copy is broken and no button is coming". A missing or half-published
    // entry resolves successfully with no content, so `isError` alone is not enough — treat "settled and
    // still nothing" as unavailable and say `failed`. Without this the only signal was the button's
    // absence, which is exactly the broken case (`fallbackReady` requires the fetch to have succeeded),
    // so a content fault left the expert on an animated skeleton for the whole call.
    const noContentAvailable = contentFailed || (!contentPending && !content);
    // The button is offered only when a fallback actually exists to reveal.
    const fallbackReady = !!filteredFallback && !contentFailed && !contentPending;

    // The expert-saw-nothing path. Without this a missing or half-published Contentful entry is silent
    // end to end: `LongFormAiFallbackShown` and `loadFallbackNarrative` sit behind `filteredFallback`,
    // and `narrativeShown` behind `session.narrative`, and the waiting report skips a miss, so on a miss
    // none of the funnel events fires and the dead-end rate is unmeasurable. Kept in a ref rather than the store because this counts content
    // faults, not sales — a remount re-reporting a still-broken entry is acceptable, and over-reporting
    // beats under-reporting a pilot-blocking outage.
    const reportedMissKey = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (session.narrative || !noContentAvailable) return;
        if (isSimulatedVoiceTask) return; // training mode must not page as though it were a live sale
        // `contentFailed` is IN the key, not just the payload: an empty settle reports first with
        // contentFailed=false, and a later refetch that throws is a different fault with a different
        // response (fix the entry vs. wait out an outage). Keyed on sale+locale alone, the second one
        // would be suppressed and on-call would chase the wrong cause.
        const key = `${sessionId}:${language}:${contentFailed}`;
        if (reportedMissKey.current === key) return;
        reportedMissKey.current = key;
        logger.error(
            { sku: VHDP_PRODUCT_SKU, language, contentFailed },
            "[LFA] no narrative content available - expert has no pitch",
        );
        void sendSalesErrorMetric("LongFormAiNarrativeContentMissing", { partner });
    }, [
        session.narrative,
        noContentAvailable,
        sessionId,
        language,
        contentFailed,
        isSimulatedVoiceTask,
        logger,
        partner,
    ]);

    // The opening state, counted. A sale opens waiting unless a producer narrative beat the mount, and the
    // V0 "showing client fallback" log that used to count openings is gone, so without this "how many
    // waits ended in a reveal" has no denominator. The guard is in the STORE, not a ref, because the
    // reveal it is divided into fires once per sale and a remount would otherwise inflate the divisor.
    // Skipped on a content miss: that state shows `failed` with no button, so it can never be revealed.
    // Dispatch BEFORE marking, as with the OPEN event, so a throwing dispatch stays retryable.
    const waitingReported = useLongFormAiStore((s) => !!s.waitingReportedFor[sessionId]);
    const markWaitingReported = useLongFormAiStore((s) => s.markWaitingReported);
    useEffect(() => {
        if (session.narrative || noContentAvailable || isSimulatedVoiceTask || waitingReported) return;
        logger.info({ sku: VHDP_PRODUCT_SKU }, "[LFA] waiting for the producer narrative");
        telemetry.waitingShown();
        markWaitingReported(sessionId);
    }, [
        session.narrative,
        noContentAvailable,
        isSimulatedVoiceTask,
        waitingReported,
        sessionId,
        markWaitingReported,
        logger,
        telemetry,
    ]);

    const { statuses } = session;
    const currentBeatId = session.currentBeatId ?? narrative?.steps[0]?.beats[0]?.id ?? "";

    // Guide "Send product details" → real guided-browsing send-link, then open the co-browse dialog
    // (mirrors GuidedBrowsingContent.handleSend). On success, HOLD the step in its "guided browsing is
    // live" state rather than advancing — the expert co-browses at their own pace and clicks Continue.
    const onGuideSend = async (_beatId: string, channel: SendChannel, value: string) => {
        if (isSimulatedVoiceTask) return; // training mode never contacts a real customer
        try {
            const response = await sendGuidedBrowsingLink(channelToCommType(channel), toContactValue(channel, value));
            // Mirrors GuidedBrowsingContent.handleSend's GuidedBrowsingLinkSent dispatch so
            // guide-send volume is comparable across both guided-browsing send paths. Gated on
            // isGuidedBrowsing: flag/Contentful-config drift (see filterNarrativeForGuidedBrowsing)
            // can route a non-guided send through this handler, and the event name must stay
            // accurate to what useHandleSendLink actually sent. productSku uses the same
            // guaranteed-string fallback as the rest of this file (session.sku ?? VHDP_PRODUCT_SKU)
            // so the field's type agrees with GuidedBrowsingContent's. currentAttempt varies per
            // send so SQS FIFO content-dedup doesn't drop a resend within the same session.
            if (isGuidedBrowsing) {
                void analytics.dispatcher.dispatchBusinessEvent("GuidedBrowsingLinkSent", {
                    sessionId,
                    partner,
                    deliveryMethod: channelToDeliveryMethod(channel),
                    productSku: session.sku ?? VHDP_PRODUCT_SKU,
                    productOfferId: selectedOfferId ?? "",
                    currentAttempt: response.currentAttempt,
                });
            }
            receiveGuidedBrowsing(sessionId, {
                url: response.url ?? "",
                sessionUrl: response.sessionUrl,
                deliveryStatus: "sent",
                deliveryMethod: channelToDeliveryMethod(channel),
            });
            // Don't show the expert the co-browse frame when the product opts out.
            if (selectedProductInfo?.guidedBrowsing?.returnLinkToExpert === false) {
                closeGuidedBrowsing(sessionId);
            }
            notification.success(selectedProductInfo?.guidedBrowsing?.sentSuccessMessage || "Link sent.");
            // Mirror onEnrollSend so guide-send volume is visible in analytics alongside enrollment sends.
            void analytics.dispatcher.dispatchBusinessEvent("ButtonClicked_SendLink");
            telemetry.guideSent();
            setAwaitingGuide(sessionId, true);
        } catch (error) {
            // Classify the attempt-cap refusal before falling through to the generic failure, the
            // same carve-out `GuidedBrowsingContent` and `onEnrollSend` already make. Without it a
            // capped expert is told to "try again" on a send the server will never allow, and the
            // refusal is counted as a frontend error. ew#7398 added the carve-out at the other three
            // send sites and missed this one; it is latent only while LFA is cohort-gated.
            if (isMaxNotificationAttemptsError(error)) {
                notification.error("You have reached the maximum number of attempts to send a link.");
                // No sendSalesErrorMetric on a cap refusal — see isMaxNotificationAttemptsError
                // in api/sendNotification.errors.ts for why, and ew#7426 for the measured volume.
            } else {
                notification.error("Failed to send guided browsing link. Please try again.");
                void sendSalesErrorMetric("GuidedBrowsingLinkError", { partner });
            }
            // Handled + recoverable (toast shown, step stays live) → WARN, matching onEnrollSend's
            // send-failure log level so recoverable send errors read consistently in dashboards.
            logger.warn({ error }, "[LFA] guided browsing send failed");
        }
    };

    // Enroll "Send enrollment" → real enrollment send-link. Completion arrives async as an
    // enrollment-event; here we just reflect the smart-link limit and hold the waiting state.
    const onEnrollSend = async (_beatId: string, channel: SendChannel, value: string) => {
        if (isSimulatedVoiceTask) return; // training mode never contacts a real customer
        try {
            const response = await sendEnrollmentLink(channelToCommType(channel), toContactValue(channel, value));
            if (selectedOfferId) {
                updateSmartLinkMeta(sessionId, selectedOfferId, {
                    maximumSmartLinksGenerated: response.maxAttemptsReached,
                });
            }
            void analytics.dispatcher.dispatchBusinessEvent("ButtonClicked_SendLink");
            telemetry.enrollSent();
            notification.success("Enrollment link sent.");
            // Hold the step in "waiting on them to enroll" — completion arrives async as an
            // enrollment-event, which flips the surface to the success screen.
            setAwaitingEnrollment(sessionId, true);
        } catch (error) {
            if (isMaxNotificationAttemptsError(error)) {
                notification.error("You have reached the maximum number of attempts to send a link.");
                // No sendSalesErrorMetric on a cap refusal — see isMaxNotificationAttemptsError
                // in api/sendNotification.errors.ts for why, and ew#7426 for the measured volume.
            } else {
                notification.error("Failed to send enrollment link. Please try again.");
                void sendSalesErrorMetric("SendLinkError", { partner });
            }
            logger.warn({ error }, "[LFA] enrollment send failed");
        }
    };

    // Decline the offer — records it in ASP (the source of the PostDeclination / DeclinationError
    // business events) and flips the product to `Declined` so `ProductSaleFlow` shows the decline
    // endgame. Inert in training (parity with `SalesFooter`, which disables Decline for a simulated
    // task). Mirrors the legacy decline in `SendLinkFooter` / `SalesFooter` — including the in-flight
    // latch (`declining`) that guards against a double-submit firing two real `v2/declination` writes.
    // Also drives `disabled` on the footer button, so the guard is honest in the affordance (not just
    // the handler) and the training-mode inertness is visible rather than a silent no-op.
    const declineDisabled = declining || isSimulatedVoiceTask || !selectedProduct;
    const onDecline = async () => {
        // Inline (not via `declineDisabled`) so TS narrows `selectedProduct` to defined below.
        if (declining || isSimulatedVoiceTask || !selectedProduct) return;
        setDeclining(true);
        try {
            await postDeclination(apiClients.sales, logger, analytics.dispatcher, {
                sessionId,
                partner,
                productOfferId: selectedProduct.id,
                productSku: selectedProduct.productSku,
                salesProvider: selectedProduct.salesProvider,
            });
            updateProduct(sessionId, selectedProduct.id, { saleProgress: SaleProgress.Declined });
            void analytics.dispatcher.dispatchBusinessEvent("ButtonClicked_Declined");
            telemetry.declined();
        } catch (error) {
            logger.warn({ error }, "[LFA] declination failed");
        } finally {
            setDeclining(false);
        }
    };

    // Return the Enroll step to the send form to send again, dispatching the re-send business event
    // the legacy waiting screen emits (`WaitForEnrollment`), so re-sends are measurable.
    const onEnrollResend = () => {
        setAwaitingEnrollment(sessionId, false);
        void analytics.dispatcher.dispatchBusinessEvent("ButtonClicked_ReSendLink");
    };

    // Quick rebuttal → show the objection/response reference INLINE in this surface (Ricardo 08-27:
    // keep the expert in the Offers flow instead of switching to the Expert Assist tab). Only offered
    // when the product actually carries rebuttal content.
    const quickRebuttalDoc = selectedProductInfo?.quickRebuttal;

    // The verizon wordmark is the header logo, so drop a redundant leading "Verizon " from the name.
    const headerProductName = productName.replace(/^Verizon\s+/i, "");
    const headerSubtitle = planData
        ? `${planData.atAGlance.monthlyPrice}/month plus tax · Home tech coverage and support`
        : undefined;

    const header = (
        <NarrativeHeader
            productName={headerProductName}
            logo={verizonLogo}
            subtitle={headerSubtitle}
            onViewPlanDetails={planData ? openPlan : undefined}
        />
    );

    const footer = (
        <NarrativeFooter
            onDecline={onDecline}
            declineDisabled={declineDisabled}
            onQuickRebuttal={
                quickRebuttalDoc
                    ? () => {
                          openRebuttal();
                          telemetry.quickRebuttalOpened();
                      }
                    : undefined
            }
        />
    );

    return (
        <>
            <NarrativeContainer
                header={header}
                overlay={
                    rebuttalOpen && quickRebuttalDoc ? (
                        <NarrativeRebuttalCard doc={quickRebuttalDoc} onDismiss={closeRebuttal} />
                    ) : undefined
                }
                footer={footer}
            >
                {narrative ? (
                    <NarrativePanel
                        narrative={narrative}
                        statuses={statuses}
                        currentBeatId={currentBeatId}
                        // Safe in this branch: a `pendingNarrative` is only ever staged when one is
                        // already displayed (`prev?.narrative && !prev.isFallback`).
                        notice={
                            session.pendingNarrative ? (
                                <NarrativeAIState state="new-script" onLoadNewScript={loadNewScript} />
                            ) : undefined
                        }
                        cues={cueNames}
                        onRead={(beatId) => {
                            markRead(sessionId, beatId);
                            telemetry.beatCovered(beatId, isBeatRequiredById(narrative, beatId));
                        }}
                        onSkip={(beatId) => {
                            markSkipped(sessionId, beatId);
                            telemetry.beatSkipped(beatId, isBeatRequiredById(narrative, beatId));
                        }}
                        onGuideSend={onGuideSend}
                        onEnrollSend={onEnrollSend}
                        sendDisabled={isSimulatedVoiceTask}
                        awaitingEnrollment={session.awaitingEnrollment}
                        onEnrollResend={onEnrollResend}
                        enrollResendDisabled={enrollResendDisabled}
                        awaitingGuide={session.awaitingGuide}
                        onGuideContinue={(beatId) => {
                            markRead(sessionId, beatId);
                            telemetry.beatCovered(beatId, isBeatRequiredById(narrative, beatId));
                            setAwaitingGuide(sessionId, false);
                        }}
                        onGuideResend={() => setAwaitingGuide(sessionId, false)}
                        onGuideReopen={(beatId) => reopenBeat(sessionId, beatId)}
                    />
                ) : (
                    // No narrative yet (fetch pending, or settled with nothing). Render the waiting
                    // state as the container's CHILD rather than returning it bare: the container owns
                    // the only footer on this surface — `ProductSaleFlow` suppresses `SalesFooter` when
                    // the LFA gate is on — so an early return here would leave the expert with ZERO
                    // footers and therefore no way to record a declination on a live call.
                    <NarrativeAIState
                        state={noContentAvailable ? "failed" : "generating"}
                        onShowFallback={fallbackReady ? showFallback : undefined}
                    />
                )}
            </NarrativeContainer>

            {planData && <PlanDetailsModal opened={planOpen} onClose={closePlan} data={planData} />}

            {/* Self-shows off useGuidedBrowsingStore once onGuideSend delivers a co-browse URL. */}
            <GuidedBrowsingModal sessionId={sessionId} logger={logger} />
        </>
    );
};
